import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Context, Lijst } from '@xxtract/tabelfilters';
import { useUsers, useDeactivateUser } from '../hooks/useUsers';
import { LAGEN } from '@xxtract/ui';
import { cn } from '@/lib/utils';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { SkeletonTable } from '@/components/feedback/SkeletonCard';
import { FilterRejectedMessage } from '@/components/feedback/FilterRejectedMessage';
import { Filterbalk, SorteerbareKop, ariaSort, useStartadres, useTabelData, useTabelStand } from '@/components/tabel';
import type { User } from '../users.api';

/** Kolommen van gebruikersbeheer (spec-tabelfilters story 5); acties staan erbuiten. */
export const GEBRUIKERS_LIJST: Lijst<User> = {
  kolommen: [
    { sleutel: 'naam', kopSleutel: 'users.table.name', type: 'tekst', waarde: (u) => `${u.firstName} ${u.lastName}`.trim() },
    { sleutel: 'email', kopSleutel: 'users.table.email', type: 'tekst', waarde: (u) => u.email },
    { sleutel: 'rol', kopSleutel: 'users.table.role', type: 'keuzelijst', waarde: (u) => u.role, waardeLabel: (w, t) => t(`users.roles.${w}`) },
    { sleutel: 'klant', kopSleutel: 'users.table.client', type: 'keuzelijst', waarde: (u) => u.gln, naamOpzoeker: true },
    {
      sleutel: 'status',
      kopSleutel: 'users.table.status',
      type: 'keuzelijst',
      waarde: (u) => (u.isActive ? 'active' : 'inactive'),
      waardeLabel: (w, t) => t(w === 'active' ? 'users.table.active' : 'users.table.inactive'),
    },
    { sleutel: 'laatsteLogin', kopSleutel: 'users.table.lastLogin', type: 'datum', waarde: (u) => u.lastLoginAt },
  ],
};
const STAND_SLEUTEL = 'gebruikers';
const FILTER_TEST_ID = 'users';
const STARTADRES = [{ lijst: GEBRUIKERS_LIJST, standSleutel: STAND_SLEUTEL }];

const KOP_STIJL = {
  backgroundColor: 'var(--background-muted)',
  fontSize: '0.6875rem',
  fontWeight: 600,
  color: 'var(--foreground-muted)',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  borderBottom: '1px solid var(--border-soft)',
} as const;

interface UserListProps {
  onEdit: (user: User) => void;
}

function formatRelativeDate(
  dateStr: string | null,
  neverLabel: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (!dateStr) return neverLabel;
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return t('users.table.today');
  if (diffDays === 1) return t('users.table.yesterday');
  if (diffDays < 7) return t('users.table.daysAgo', { count: diffDays });
  if (diffDays < 30) return t('users.table.weeksAgo', { count: Math.floor(diffDays / 7) });
  return date.toLocaleDateString();
}

export function UserList({ onEdit }: UserListProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError, error, refetch } = useUsers();
  const deactivateUser = useDeactivateUser();
  const context = useMemo<Context>(() => ({ t }), [t]);
  const { start, geweigerd, wisGeweigerd } = useStartadres(STARTADRES);
  const stand = useTabelStand(GEBRUIKERS_LIJST, STAND_SLEUTEL, { start: start[STAND_SLEUTEL] });
  const tabel = useTabelData(data?.data, GEBRUIKERS_LIJST, stand, context);
  const lijstRef = useRef<HTMLDivElement>(null);
  // Deactiveren ging eerder onmiddellijk bij het aanklikken (gemeten 26-08-2026). Eén misklik in
  // de verkeerde regel zette meteen iemand op inactief. De knop zet nu eerst deze vraag klaar.
  const [teDeactiveren, setTeDeactiveren] = useState<User | null>(null);
  // Waar de focus stond toen het venster openging, zodat hij daar terugkomt bij het sluiten.
  const knopVoorHetVenster = useRef<HTMLElement | null>(null);
  const annuleerKnop = useRef<HTMLButtonElement | null>(null);

  const sluitVenster = () => {
    setTeDeactiveren(null);
    // Zonder dit blijft de focus achter op het document en moet een toetsenbordgebruiker van
    // voren af aan door de pagina tabben.
    knopVoorHetVenster.current?.focus();
  };

  // Ontsnappen met Escape hoort bij elk venster; zonder is het alleen met de muis te sluiten.
  useEffect(() => {
    if (!teDeactiveren) return;
    const bijToets = (e: KeyboardEvent) => {
      if (e.key === 'Escape') sluitVenster();
    };
    document.addEventListener('keydown', bijToets);
    return () => document.removeEventListener('keydown', bijToets);
  });

  // De focus in het venster leggen, zodat hulpsoftware de vraag voorleest.
  useEffect(() => {
    if (teDeactiveren) annuleerKnop.current?.focus();
  }, [teDeactiveren]);

  if (isLoading) return <SkeletonTable rows={6} />;
  if (isError) return <ApiErrorMessage error={error} onRetry={() => refetch()} />;

  const filtered = tabel.rijen;

  const roleBadgeColor: Record<string, { bg: string; color: string }> = {
    admin: { bg: 'var(--primary-light)', color: 'var(--primary)' },
    employee: { bg: '#E0F2F1', color: '#54949E' },
    customer: { bg: '#F1F5F9', color: '#64748B' },
  };

  return (
    <div data-testid="user-list" ref={lijstRef} tabIndex={-1} className="outline-none">
      <div className="mb-4 flex flex-col gap-3">
        <FilterRejectedMessage
          baseTestId={FILTER_TEST_ID}
          focusNaWissen={lijstRef}
          linkGeweigerd={geweigerd}
          onClear={() => {
            stand.reset();
            wisGeweigerd();
          }}
        />
        <Filterbalk context={context} data={tabel} lijst={GEBRUIKERS_LIJST} stand={stand} />
      </div>

      {/* Table: scrolls in its own container, never the page (375 px) */}
      <div
        className="overflow-x-auto rounded-xl"
        style={{
          backgroundColor: 'var(--background)',
          border: '1px solid var(--border-soft)',
          boxShadow: 'var(--card-shadow)',
        }}
      >
        <table data-testid="users-table" className="w-full">
          <thead>
            <tr>
              {GEBRUIKERS_LIJST.kolommen.map((kolom) => (
                <th
                  key={kolom.sleutel}
                  aria-label={t(kolom.kopSleutel)}
                  aria-sort={ariaSort(stand.sortering, kolom.sleutel)}
                  className="whitespace-nowrap px-4 py-2.5 text-left"
                  style={KOP_STIJL}
                >
                  <SorteerbareKop kolom={kolom} onKlik={() => stand.klikOpKop(kolom.sleutel)} sortering={stand.sortering} />
                </th>
              ))}
              <th className="whitespace-nowrap px-4 py-2.5 text-left" style={KOP_STIJL}>
                {t('common.actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center">
                  <span className="text-sm" style={{ color: 'var(--foreground-muted)' }}>
                    {t('common.noResults')}
                  </span>
                </td>
              </tr>
            ) : (
              filtered.map((user) => (
                <tr
                  key={user.id}
                  data-testid={`user-row-${user.id}`}
                  className="transition-all duration-100"
                  style={{ opacity: user.isActive ? 1 : 0.5 }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLTableRowElement).style.backgroundColor =
                      'var(--primary-faint)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLTableRowElement).style.backgroundColor = '';
                  }}
                >
                  {/* Name */}
                  <td
                    className="px-4 py-3 text-[0.8125rem] font-medium"
                    style={{ borderBottom: '1px solid var(--border-soft)', color: 'var(--foreground)' }}
                  >
                    {user.firstName} {user.lastName}
                  </td>
                  {/* Email */}
                  <td
                    className="px-4 py-3 text-[0.8125rem]"
                    style={{ borderBottom: '1px solid var(--border-soft)', color: 'var(--foreground-muted)' }}
                  >
                    {user.email}
                  </td>
                  {/* Role */}
                  <td
                    className="px-4 py-3"
                    style={{ borderBottom: '1px solid var(--border-soft)' }}
                  >
                    <span
                      className="inline-flex items-center rounded-full px-2 py-[3px] text-[0.6875rem] font-medium"
                      style={roleBadgeColor[user.role] ?? roleBadgeColor.customer}
                    >
                      {t(`users.roles.${user.role}`)}
                    </span>
                  </td>
                  {/* GLN / Client */}
                  <td
                    className="px-4 py-3 text-[0.8125rem]"
                    style={{ borderBottom: '1px solid var(--border-soft)', color: 'var(--foreground-muted)' }}
                  >
                    {user.gln ?? '—'}
                  </td>
                  {/* Status */}
                  <td
                    className="px-4 py-3"
                    style={{ borderBottom: '1px solid var(--border-soft)' }}
                  >
                    <span
                      data-testid="user-status-badge"
                      className="inline-flex items-center gap-1 rounded-full px-2 py-[3px] text-[0.6875rem] font-medium"
                      style={{
                        backgroundColor: user.isActive ? 'var(--success-light)' : '#F1F5F9',
                        color: user.isActive ? 'var(--success-dark)' : '#64748B',
                      }}
                    >
                      <span
                        className="inline-block h-[5px] w-[5px] rounded-full"
                        style={{
                          backgroundColor: user.isActive ? 'var(--success-dark)' : '#94A3B8',
                        }}
                      />
                      {user.isActive ? t('users.table.active') : t('users.table.inactive')}
                    </span>
                  </td>
                  {/* Last Login */}
                  <td
                    className="px-4 py-3 text-[0.8125rem]"
                    style={{ borderBottom: '1px solid var(--border-soft)', color: 'var(--foreground-muted)' }}
                  >
                    {formatRelativeDate(user.lastLoginAt, t('users.table.never'), t)}
                  </td>
                  {/* Actions */}
                  <td
                    className="px-4 py-3"
                    style={{ borderBottom: '1px solid var(--border-soft)' }}
                  >
                    <div className="flex gap-1">
                      <button
                        data-testid={`edit-user-${user.id}`}
                        onClick={() => onEdit(user)}
                        className="rounded-lg p-1.5 transition-all duration-100"
                        style={{ color: 'var(--foreground-muted)' }}
                        aria-label={t('users.table.edit')}
                        title={t('users.table.edit')}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                          edit
                        </span>
                      </button>
                      {user.isActive && (
                        <button
                          data-testid={`deactivate-user-${user.id}`}
                          onClick={(e) => {
                            knopVoorHetVenster.current = e.currentTarget;
                            setTeDeactiveren(user);
                          }}
                          className="rounded-lg p-1.5 transition-all duration-100"
                          style={{ color: 'var(--destructive)' }}
                          aria-label={t('users.table.deactivate')}
                          title={t('users.table.deactivate')}
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                            person_off
                          </span>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {teDeactiveren && (
        <>
          <div
            className={cn('fixed inset-0 bg-black/30', LAGEN.venster)}
            onClick={sluitVenster}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="deactivate-confirm-title"
            data-testid="deactivate-confirm-dialog"
            className={cn(
              'fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl p-6',
              LAGEN.venster,
            )}
            style={{ backgroundColor: 'var(--background)', boxShadow: 'var(--card-shadow-md)' }}
          >
            <h3
              id="deactivate-confirm-title"
              className="mb-3 text-base font-semibold"
              style={{ color: 'var(--foreground-heading)' }}
            >
              {t('users.deactivateDialog.title')}
            </h3>
            <p className="mb-5 text-[0.8125rem]" style={{ color: 'var(--foreground)' }}>
              {t('users.deactivateDialog.body', {
                name: `${teDeactiveren.firstName} ${teDeactiveren.lastName}`.trim(),
              })}
            </p>
            <div className="flex justify-end gap-2">
              <button
                ref={annuleerKnop}
                onClick={sluitVenster}
                data-testid="deactivate-cancel-button"
                className="rounded-lg px-4 py-2 text-[0.8125rem] font-medium"
                style={{ border: '1px solid var(--border)', color: 'var(--foreground)' }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => {
                  deactivateUser.mutate(teDeactiveren.id);
                  sluitVenster();
                }}
                data-testid="deactivate-confirm-button"
                className="rounded-lg px-4 py-2 text-[0.8125rem] font-medium text-white"
                style={{ backgroundColor: 'var(--destructive)' }}
              >
                {t('users.deactivateDialog.confirm')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
